import {
  type JSX,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AGENT_DATA_FOLDER_MESSAGES,
  type CheckRunEnvironmentDataRootsResponseBody,
  type RunEnvironmentTransport,
} from "common";
import {
  useCheckDataRoots,
  useDataRoots,
  useDataRootsImpact,
  useReplaceDataRoots,
} from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import {
  backsDataSourcesWarning,
  CHECK_STATUS_TEXT,
  type DataFolderRow,
  newDataFolderRow,
  rowsFromRoots,
  sameDataFolderRows,
  toReplaceBody,
  validateDataFolderRows,
} from "@via/pages/account/profile/run-environments/run-environment-form/data-folders.ts";

type ErrorResponse = {
  response?: { status?: number; data?: { detail?: string; message?: string } };
};
const statusOf = (error: unknown): number | undefined =>
  (error as ErrorResponse)?.response?.status;
const detailOf = (error: unknown): string =>
  (error as ErrorResponse)?.response?.data?.detail ??
  (error as ErrorResponse)?.response?.data?.message ??
  "The data folders could not be saved.";
const checkDetailOf = (error: unknown): string =>
  (error as ErrorResponse)?.response?.data?.detail ??
  (error as ErrorResponse)?.response?.data?.message ??
  "the agent did not answer";

/**
 * This card sits inside the run environment form. Every button is type="button" and Enter in an
 * input is swallowed, so nothing here submits the whole run environment. The rows are local
 * state, never registered with react hook form: they are saved by their own PUT, independently
 * of the form's Save.
 */
const swallowEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
  if (event.key === "Enter") event.preventDefault();
};

const inputClass =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";
const secondaryButton =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

export function DataFoldersCard({
  environmentId,
  transport,
  suggestions = [],
}: {
  environmentId?: number;
  transport?: RunEnvironmentTransport;
  /** Rows to prefill when nothing is saved yet. Read once, on the first load of the saved set. */
  suggestions?: DataFolderRow[];
}): JSX.Element {
  const roots = useDataRoots(environmentId);
  const check = useCheckDataRoots(environmentId);
  const impact = useDataRootsImpact(environmentId);
  const replace = useReplaceDataRoots(environmentId);

  const [rows, setRows] = useState<DataFolderRow[]>([]);
  const [checkResult, setCheckResult] =
    useState<CheckRunEnvironmentDataRootsResponseBody | null>(null);
  const [pendingOutside, setPendingOutside] = useState<{
    outside: number;
    unchecked: number;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);

  const [prefilled, setPrefilled] = useState(false);
  // The suggestions as they stood when the card mounted. Prefilling happens once, on the first
  // load of an empty saved set: a later refetch (after the owner saved an empty list on purpose,
  // say) must show what is saved, not bring the suggestions back.
  const initialSuggestions = useRef(suggestions);
  const prefillDecided = useRef(false);

  useEffect(() => {
    if (!roots.data) return;
    const prefill =
      !prefillDecided.current &&
      roots.data.length === 0 &&
      initialSuggestions.current.length > 0;
    prefillDecided.current = true;
    setRows(prefill ? initialSuggestions.current : rowsFromRoots(roots.data));
    setPrefilled(prefill);
  }, [roots.data]);

  if (!environmentId) {
    return (
      <p className="text-sm text-gray-600">
        Save this run environment first, then declare the folders its users can
        browse as a data source.
      </p>
    );
  }
  if (roots.isLoading)
    return <p className="text-sm text-gray-600">Loading data folders</p>;
  if (roots.isError) {
    return (
      <p className="text-sm text-gray-600">
        {statusOf(roots.error) === 403
          ? "Only the run environment's owner, an admin of its organization or a platform admin can manage its data folders."
          : "The data folders could not be loaded."}
      </p>
    );
  }

  const errors = validateDataFolderRows(rows);
  const invalid = errors.some(error => error !== null);
  const paths = rows.map(row => row.path);
  // Checking and saving are independent requests: a pending check must never disable Save, so the
  // wait for a slow or silent agent (spec 4.3) does not also block the button that does not need it.
  const checking = check.isPending;
  const saving = impact.isPending || replace.isPending;
  // PUT replaces the whole set and bumps the access configuration version, which can turn pending
  // approvals review_stale, so Save is offered only when the rows differ from what is stored.
  const unchanged = sameDataFolderRows(rows, rowsFromRoots(roots.data ?? []));
  // Check folders goes through the owner's agent; the server refuses it on any other transport.
  const canCheck = "agent" === transport;

  const edit = (index: number, patch: Partial<DataFolderRow>): void => {
    setRows(previous =>
      previous.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
    setCheckResult(null);
    setCheckError(null);
    setPendingOutside(null);
    setSavedNotice(false);
  };
  const remove = (index: number): void => {
    setRows(previous => previous.filter((_, i) => i !== index));
    setCheckResult(null);
    setCheckError(null);
    setPendingOutside(null);
    setSavedNotice(false);
  };
  const addRow = (): void => {
    setRows(previous => [...previous, newDataFolderRow()]);
    setCheckResult(null);
    setCheckError(null);
    setPendingOutside(null);
    setSavedNotice(false);
  };

  const runCheck = async (): Promise<void> => {
    setCheckError(null);
    try {
      setCheckResult(await check.mutateAsync(paths));
    } catch (error) {
      setCheckError(`Folders could not be checked: ${checkDetailOf(error)}`);
    }
  };

  const save = async (): Promise<void> => {
    setSaveError(null);
    try {
      // Always the full list: the PUT replaces every stored folder with exactly these rows.
      await replace.mutateAsync(toReplaceBody(rows));
      setPendingOutside(null);
      setPrefilled(false);
      setSavedNotice(true);
    } catch (error) {
      setSaveError(detailOf(error));
    }
  };

  const requestSave = async (): Promise<void> => {
    setSaveError(null);
    try {
      const { dataSourcesOutside, dataSourcesUnchecked = 0 } =
        await impact.mutateAsync(paths);
      if (dataSourcesOutside > 0 || dataSourcesUnchecked > 0) {
        setPendingOutside({
          outside: dataSourcesOutside,
          unchecked: dataSourcesUnchecked,
        });
        return;
      }
    } catch (error) {
      setSaveError(detailOf(error));
      return;
    }
    await save();
  };

  const statusFor = (path: string) =>
    checkResult?.results.find(result => result.path === path);

  return (
    <div className="space-y-4" data-testid="data-folders-card">
      {canCheck ? (
        <p className="text-sm text-gray-600">
          Folders on the host that users of this run environment can browse and
          add to datasets. Each user browses through their own agent, so the
          host decides what each account can read inside these folders. A folder
          starting with ~ is in each user&apos;s own home on the host.
        </p>
      ) : (
        <p className="text-sm text-gray-600">
          Folders on the host that users of this run environment can work with.
          The host decides what each account can read inside these folders. A
          folder starting with ~ is in each user&apos;s own home on the host.
        </p>
      )}
      {prefilled ? (
        <p
          className="text-sm text-amber-700"
          data-testid="data-folders-prefilled">
          Suggested folders are filled in from this run environment&apos;s
          directories. Nothing is saved until you press Save data folders.
        </p>
      ) : null}
      {rows.map((row, index) => {
        const result = statusFor(row.path);
        return (
          <div key={index} className="space-y-1">
            <div className="flex items-start gap-2">
              <input
                aria-label={`Folder path ${index + 1}`}
                className={inputClass}
                value={row.path}
                placeholder="/absolute/folder or ~/folder"
                onKeyDown={swallowEnter}
                onChange={event => edit(index, { path: event.target.value })}
              />
              <input
                aria-label={`Folder label ${index + 1}`}
                className={inputClass}
                value={row.label}
                placeholder="Label (optional)"
                onKeyDown={swallowEnter}
                onChange={event => edit(index, { label: event.target.value })}
              />
              <button
                type="button"
                className={secondaryButton}
                aria-label={`Remove folder ${index + 1}`}
                onClick={() => remove(index)}>
                Remove
              </button>
            </div>
            {errors[index] ? (
              <p className="text-xs text-rose-500">{errors[index]}</p>
            ) : null}
            {result ? (
              <p
                className={
                  result.status === "ok"
                    ? "text-xs text-green-700"
                    : "text-xs text-rose-600"
                }>
                {CHECK_STATUS_TEXT[result.status]}
                {result.detail ? `: ${result.detail}` : ""}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2">
        <button type="button" className={secondaryButton} onClick={addRow}>
          Add folder
        </button>
        {canCheck ? (
          <button
            type="button"
            className={secondaryButton}
            disabled={invalid || rows.length === 0 || checking || saving}
            onClick={() => void runCheck()}>
            Check folders
          </button>
        ) : null}
        <button
          type="button"
          className={primaryButton}
          disabled={invalid || saving || unchanged}
          onClick={() => void requestSave()}>
          Save data folders
        </button>
      </div>
      {checkError ? (
        <p className="text-sm text-rose-600">{checkError}</p>
      ) : null}

      {checkResult?.agent === "not_connected" ? (
        <p className="text-sm text-amber-700">
          {AGENT_DATA_FOLDER_MESSAGES.notConnected}, so the folders cannot be
          checked. Saving still works.
        </p>
      ) : null}
      {checkResult?.agent === "upgrade_required" ? (
        <p className="text-sm text-amber-700">
          Update the agent on this host to check folders. Saving still works.
        </p>
      ) : null}
      {checkResult?.agent === "connected" ? (
        <p className="text-xs text-gray-500">
          Checked as your own account on the host. Other users see only what
          their own account can read.
        </p>
      ) : null}

      {pendingOutside !== null ? (
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          <p>
            {backsDataSourcesWarning(
              pendingOutside.outside,
              pendingOutside.unchecked
            )}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className={primaryButton}
              disabled={saving}
              onClick={() => void save()}>
              Save anyway
            </button>
            <button
              type="button"
              className={secondaryButton}
              onClick={() => setPendingOutside(null)}>
              Keep editing
            </button>
          </div>
        </div>
      ) : null}
      {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
      {savedNotice ? (
        <p className="text-sm text-green-700">Data folders saved.</p>
      ) : null}
    </div>
  );
}
